import { CircularProgress, IconButton, Stack } from "@mui/material";
import {
  AnchorNav,
  Button,
  DrawerContent,
  DrawerNavBar,
  Typography,
} from "@platform/design-system";
import CloseIcon from "@mui/icons-material/Close";
import { FormProvider, useForm } from "react-hook-form";
import { useMemo, useState } from "react";
import z from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";

import useToastNotificationService from "../../../../hooks/useToastNotificationService";
import {
  useScenarioMutation,
  useScenariosQuery,
} from "../../../../hooks/useBbe";
import { Scenario } from "../../types";

import { ScenarioNoteField } from "./ScenarioNoteField";
import { CancelModal } from "./CancelModal";

type ScenarioNotesDrawerProps = {
  data: {
    modelId: string;
    scenarioId?: string;
  };
  onClose: VoidFunction;
};

const SCENARIO_NOTE_MAX_LENGTH = 4096;

export const ScenarioNotesDrawer: React.FC<ScenarioNotesDrawerProps> = ({
  data,
  onClose,
}) => {
  const { modelId, scenarioId } = data;
  const { createToastNotification } = useToastNotificationService();
  const queryClient = useQueryClient();
  const scenarioNoteMutation = useScenarioMutation();

  const [cancelModalOpen, setCancelModalOpen] = useState<boolean>(false);

  const scenariosQuery = useScenariosQuery(modelId);
  const isLoading = !modelId || scenariosQuery.isPending;
  const isError = scenariosQuery.isError;
  const scenarioNotes: Scenario[] = useMemo(
    () => scenariosQuery.data?.scenarios ?? [],
    [scenariosQuery.data],
  );

  const schema = z.object(
    Object.fromEntries(
      scenarioNotes.map((s) => [
        s.id,
        z.string().max(SCENARIO_NOTE_MAX_LENGTH),
      ]),
    ),
  );

  const values: Record<string, string> = useMemo(
    () => Object.fromEntries(scenarioNotes.map((s) => [s.id, s.description])),
    [scenarioNotes],
  );

  const methods = useForm({
    values,
    resetOptions: { keepDirtyValues: true, keepErrors: true },
    resolver: zodResolver(schema),
  });

  const {
    formState: { isDirty },
  } = methods;

  const handleClose = (): void => {
    if (isDirty) {
      setCancelModalOpen(true);
    } else {
      onClose();
    }
  };

  const anchorItems = scenarioNotes.map((note) => ({
    id: note.name,
    label: note.name,
  }));

  const initialActiveNote = scenarioId
    ? scenarioNotes.find((n) => n.id === scenarioId)
    : undefined;

  const onSubmit = methods.handleSubmit(
    async (values: Record<string, string>) => {
      const dirtyFields = methods.formState.dirtyFields;
      const updates = Object.keys(dirtyFields).map((scenarioId) => ({
        scenarioId,
        description: values[scenarioId],
      }));

      try {
        await Promise.all(
          updates.map((update) => scenarioNoteMutation.mutateAsync(update)),
        );
        await queryClient.invalidateQueries({
          queryKey: ["bbe", "scenarios", modelId],
        });
        createToastNotification({
          severity: "success",
          message: "Scenario notes saved successfully.",
        });
        methods.reset(values);
        onClose();
      } catch {
        createToastNotification({
          severity: "error",
          message: "Failed to save scenario notes. Please try again.",
        });
      }
    },
  );

  return (
    <FormProvider {...methods}>
      <DrawerNavBar>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          width="100%"
        >
          <Typography variant="body-large" color="textSecondary">
            Note
          </Typography>
          <IconButton onClick={handleClose}>
            <CloseIcon fontSize="small" aria-label="Close drawer" />
          </IconButton>
        </Stack>
      </DrawerNavBar>

      <DrawerContent>
        {isLoading ? (
          <Stack alignItems="center" justifyContent="center" flex={1}>
            <CircularProgress />
          </Stack>
        ) : isError ? (
          <Stack alignItems="center" justifyContent="center" flex={1}>
            <Typography color="danger">
              Failed to load scenario notes. Please try again.
            </Typography>
          </Stack>
        ) : (
          <AnchorNav
            anchorMenuItems={anchorItems}
            anchorMenuTitle="My Model"
            menuPosition="right"
            initialActiveId={initialActiveNote?.name}
            AnchorNavSxProps={{
              "& > :last-child": {
                flexShrink: 1,
                minWidth: 0,
                maxWidth: "30%",
              },
            }}
          >
            {({ scrollContainerRef, setSectionRef }) => (
              <Stack
                direction="column"
                spacing={3}
                flex={1}
                ref={scrollContainerRef}
                sx={{ paddingRight: 2, overflow: "auto", minHeight: 0 }}
              >
                {scenarioNotes.map((scenario) => (
                  <ScenarioNoteField
                    onRef={setSectionRef}
                    label={scenario.name}
                    fieldName={scenario.id}
                    key={scenario.id}
                    maxLength={SCENARIO_NOTE_MAX_LENGTH}
                  />
                ))}
              </Stack>
            )}
          </AnchorNav>
        )}
      </DrawerContent>
      <DrawerNavBar bottom>
        <Stack
          direction="row"
          spacing={2}
          justifyContent="flex-end"
          alignItems="center"
          width="100%"
        >
          <Button variant="tertiary" onClick={handleClose}>
            Cancel
          </Button>

          <Button
            variant="primary"
            onClick={onSubmit}
            disabled={isLoading || isError}
          >
            Save
          </Button>
        </Stack>
      </DrawerNavBar>

      <CancelModal
        open={cancelModalOpen}
        setOpen={setCancelModalOpen}
        confirmChange={onClose}
      />
    </FormProvider>
  );
};
